import React, { useState } from 'react';
import { sectionTitleSx } from '../../styles/typography';
import {
    analyticsCompactBadgeSx,
    analyticsPanelSx,
} from '../../styles/analyticsMsiStyles';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FileUploader from '../FileUploader';
import { FILE_UPLOAD_RESULT_KINDS } from '../fileUploadResult';
import { callBackend } from '../../utils/api';
import { buildCanonicalPosSizingPortfolioRows } from '../../utils/posSizingPortfolioContract';
import { Grid2 } from '@mui/material';
import {
    IDENTIFIER_COLUMNS,
    requiredColumnsUploadConfig,
    validateExactlyOneUploadIdentifierColumn,
} from '../../config/UploadConstant';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { cardClasses } from '../../styles/tailwindClasses';
import {
    POS_SIZING_ATOMIC_PUBLICATION_CONTRACT,
    POS_SIZING_FILE_UPLOAD_MODE,
    requireAtomicPublicationReceipt,
    requirePosSizingPublicationCapability,
} from '../../utils/posSizingPublicationContract';

const buildCanonicalPortfolioRows = (results) => {
    const rows = Array.isArray(results?.pos_sizing) ? results.pos_sizing : null;
    const droppedCalculationIds = results?.dropped_cognive_ids_from_calc;
    if (
        !rows
        || rows.length < 1
        || !Array.isArray(droppedCalculationIds)
        || droppedCalculationIds.length > 0
    ) {
        throw new Error('Position sizing calculation did not return the complete upload scope.');
    }

    return buildCanonicalPosSizingPortfolioRows(rows);
};

export default function PosSizingUploader({
    buildPublicationFields,
    onUploadSuccess,
    onUploadBusyChange,
    gridSize = { xs: 12, md: 12 },
    variant = 'panel',
    onInteraction,
}) {
    const formatBackendError = (error) => {
        const message = error?.message ?? String(error ?? '');
        const jsonStart = message.indexOf('{');
        if (jsonStart >= 0) {
            try {
                const parsed = JSON.parse(message.slice(jsonStart));
                const detail = parsed?.detail;
                if (typeof detail === 'string') {
                    return detail;
                }
                if (detail && typeof detail === 'object') {
                    return detail?.message || detail?.code || parsed?.message || message;
                }
                return parsed?.message || message;
            } catch (_) {
                return message;
            }
        }
        return message;
    };

    const [snackBarData, setSnackBarData] = useState({ open: false, message: '', severity: 'info' });

    async function handleFileUpload(prepared) {
        onUploadBusyChange?.(true);
        try {
            validateExactlyOneUploadIdentifierColumn(prepared.headerRow || [], IDENTIFIER_COLUMNS);
            if (typeof buildPublicationFields !== 'function') {
                throw new Error('Position Sizing settings are unavailable. Refresh and try again.');
            }

            await requirePosSizingPublicationCapability(
                callBackend,
                POS_SIZING_FILE_UPLOAD_MODE,
            );
            // An upload publishes a fresh context like Sync Draft, so it can
            // also create a Watchlist-only user's first draft.
            const resp = await callBackend('publish_pos_sizing_v1', 'POST', {
                rows: prepared.parsedRows,
                ...buildPublicationFields(),
                contract: POS_SIZING_ATOMIC_PUBLICATION_CONTRACT,
                mode: POS_SIZING_FILE_UPLOAD_MODE,
            });
            if (resp && resp.success && resp.results) {
                const { identifier_column: identifierColumn } = resp.results;
                const canonicalPortfolioRows = buildCanonicalPortfolioRows(resp.results);
                requireAtomicPublicationReceipt(
                    resp,
                    canonicalPortfolioRows,
                    POS_SIZING_FILE_UPLOAD_MODE,
                );
                const successCount = canonicalPortfolioRows.length;
                const excludedIdentifiers = Array.isArray(resp?.receipt?.excluded_identifiers)
                    ? resp.receipt.excluded_identifiers.filter(Boolean)
                    : [];
                const exclusionSuffix = excludedIdentifiers.length
                    ? ` ${excludedIdentifiers.length} unavailable securit${excludedIdentifiers.length === 1 ? 'y was' : 'ies were'} not added: ${excludedIdentifiers.join(', ')}.`
                    : '';

                setSnackBarData({
                    open: true,
                    message: `Successfully replaced ${successCount} row${successCount === 1 ? '' : 's'}${identifierColumn ? ` using ${identifierColumn.toUpperCase()} identifiers` : ''}.${exclusionSuffix}`,
                    severity: excludedIdentifiers.length ? 'warning' : 'success',
                });
                try {
                    await onUploadSuccess?.(resp.results, resp.receipt);
                } catch (refreshError) {
                    console.error('Position sizing saved, but the dashboard refresh failed:', refreshError);
                    setSnackBarData({
                        open: true,
                        message: 'Position sizing was saved, but the dashboard could not refresh it yet.',
                        severity: 'warning',
                    });
                }
                return { kind: FILE_UPLOAD_RESULT_KINDS.COMPLETED };
            } else {
                throw new Error(resp?.message || 'Error uploading position sizing file.');
            }
        } catch (err) {
            const message = formatBackendError(err);
            setSnackBarData({ open: true, message, severity: 'error' });
            return { kind: FILE_UPLOAD_RESULT_KINDS.FAILED, message };
        } finally {
            onUploadBusyChange?.(false);
        }
    }

    const uploaderContent = (
        <>
            <FileUploader
                uploaderKey="posSizing"
                requiredColumns={requiredColumnsUploadConfig.posSizing}
                helperText={`Required: trade_idea plus exactly one identifier column (${IDENTIFIER_COLUMNS.join(', ')}). Ticker labels are sourced from your watchlist.`}
                onFileUpload={handleFileUpload}
                labelName='Pos Sizing'
                compact
                compactVariant={variant === 'header' ? 'dropdown' : 'inline'}
                showTitle={false}
                onInteraction={onInteraction}
            />
            <Snackbar open={snackBarData.open} autoHideDuration={6000} onClose={() => setSnackBarData({ ...snackBarData, open: false })}>
                <Alert severity={snackBarData.severity} onClose={() => setSnackBarData({ ...snackBarData, open: false })}>
                    {snackBarData.message}
                </Alert>
            </Snackbar>
        </>
    );

    if (variant === 'header' || variant === 'expanded') {
        return uploaderContent;
    }

    return (
        <Grid2 size={gridSize}>
            <Paper className={cardClasses} elevation={1} sx={analyticsPanelSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CloudUploadOutlinedIcon fontSize="small" color="action" />
                        <Typography
                            variant="subtitle1"
                            sx={sectionTitleSx}
                        >
                            Pos Sizing Upload
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <Chip size="small" variant="outlined" label="Manual Upload" sx={analyticsCompactBadgeSx} />
                        <Chip size="small" variant="outlined" label="Replace Draft" sx={analyticsCompactBadgeSx} />
                    </Box>
                </Box>
                {uploaderContent}
            </Paper>
        </Grid2>
    );
}
